import React, { useState, KeyboardEvent } from 'react';

export interface QuickTag {
  id: string;
  label: string;
  color: string;
}

interface QuickTagInputProps {
  tags: QuickTag[];
  selectedTagIds: string[];
  onTagCreate: (label: string) => void;
  onTagSelect: (tagId: string) => void;
  onTagDeselect: (tagId: string) => void;
}

// Color palette for tags (auto-assigned)
const TAG_COLORS = [
  'bg-blue-100 text-blue-800 border-blue-200',
  'bg-green-100 text-green-800 border-green-200',
  'bg-purple-100 text-purple-800 border-purple-200',
  'bg-yellow-100 text-yellow-800 border-yellow-200',
  'bg-pink-100 text-pink-800 border-pink-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
  'bg-red-100 text-red-800 border-red-200',
  'bg-orange-100 text-orange-800 border-orange-200',
  'bg-teal-100 text-teal-800 border-teal-200',
  'bg-cyan-100 text-cyan-800 border-cyan-200',
];

// Selected tag styles (darker/more prominent)
const SELECTED_TAG_COLORS = [
  'bg-blue-500 text-white border-blue-600',
  'bg-green-500 text-white border-green-600',
  'bg-purple-500 text-white border-purple-600',
  'bg-yellow-500 text-white border-yellow-600',
  'bg-pink-500 text-white border-pink-600',
  'bg-indigo-500 text-white border-indigo-600',
  'bg-red-500 text-white border-red-600',
  'bg-orange-500 text-white border-orange-600',
  'bg-teal-500 text-white border-teal-600',
  'bg-cyan-500 text-white border-cyan-600',
];

const QuickTagInput: React.FC<QuickTagInputProps> = ({
  tags,
  selectedTagIds,
  onTagCreate,
  onTagSelect,
  onTagDeselect
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      onTagCreate(inputValue.trim());
      setInputValue('');
    }
  };

  const handleTagClick = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onTagDeselect(tagId);
    } else {
      onTagSelect(tagId);
    }
  };

  const getTagColor = (tag: QuickTag, isSelected: boolean) => {
    // Use a consistent color index based on tag label (so same tag always has same color)
    const colorIndex = tag.label.charCodeAt(0) % TAG_COLORS.length;
    return isSelected 
      ? SELECTED_TAG_COLORS[colorIndex] 
      : TAG_COLORS[colorIndex];
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      {/* Heading */}
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Manual Tags</h3>
      
      {/* Input Field */}
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type tag name and press Enter..."
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm mb-3"
      />

      {/* Tags Display */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => {
            const isSelected = selectedTagIds.includes(tag.id);
            return (
              <button
                key={tag.id}
                onClick={() => handleTagClick(tag.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                  getTagColor(tag, isSelected)
                } ${isSelected ? 'ring-2 ring-offset-1' : 'hover:opacity-80'}`}
                title={isSelected ? 'Click to deselect' : 'Click to select'}
              >
                {tag.label}
                {isSelected && (
                  <span className="ml-1">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {tags.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-2">
          No tags yet. Type a tag name and press Enter to create one.
        </p>
      )}
    </div>
  );
};

export default QuickTagInput;

